// database.js

import { openDB } from 'idb';

import { Sentry } from '@/vendors/Sentry';

const dbName = 'voizor-sales-ui-database';

const recordingsStoreName = 'voizor-sales-ui-recordings';
const peaksStoreName = 'voizor-sales-ui-peaks';
const cachedAudioStoreName = 'voizor-sales-ui-cached-audio';
const version = 6;

// Initialize the IndexedDB using idb
async function initDB() {
  return openDB(dbName, version, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(peaksStoreName)) {
        const store = db.createObjectStore(peaksStoreName);
        store.createIndex('timestamp', 'timestamp');
      }
      if (!db.objectStoreNames.contains(cachedAudioStoreName)) {
        const store = db.createObjectStore(cachedAudioStoreName);
        store.createIndex('timestamp', 'timestamp');
      }
      if (!db.objectStoreNames.contains(recordingsStoreName)) {
        db.createObjectStore(recordingsStoreName);
      }
    },
  });
}

async function addData(key, value, _storeName) {
  try {
    const db = await initDB();
    const tx = db.transaction(_storeName, 'readwrite');
    const store = tx.objectStore(_storeName);
    await store.add(value, key);
    await tx.done;
  } catch (error) {
    console.error('addData', error);
    Sentry.captureException(error);
  }
}

// Save data to IndexedDB
async function setData(key, value, _storeName) {
  try {
    const db = await initDB();
    const tx = db.transaction(_storeName, 'readwrite');
    const store = tx.objectStore(_storeName);
    await store.put(value, key);
    await tx.done;
  } catch (error) {
    console.error('setData', error);
    Sentry.captureException(error);
  }
}

async function setCacheData(key, value, _storeName) {
  const timestamp = Date.now();
  return setData(key, { value, timestamp }, _storeName);
}

async function readCacheData(key, _storeName) {
  const data = await readData(key, _storeName);
  if (data) {
    return data.value;
  }
}

// Clean up old blobs based on timestamp for a given store
async function cleanCachedData(storeName) {
  try {
    const db = await initDB();
    const tx = db.transaction(storeName, 'readwrite');
    const index = tx.store.index('timestamp');
    const now = Date.now();
    const exceedDuration = 12 * 60 * 60 * 1000; // Twelwe hours in milliseconds
    for await (const cursor of index.iterate()) {
      console.debug(cursor.value.timestamp);
      if (now - cursor.value.timestamp > exceedDuration) {
        await cursor.delete();
      }
    }

    await tx.done;
  } catch (error) {
    console.error('Error cleaning IndexedDB cache:', error);
    Sentry.captureException(error);
  }
}

// Run cleanup on application startup for both stores
async function cleanAllCachedData() {
  await cleanCachedData(cachedAudioStoreName);
  await cleanCachedData(peaksStoreName);
}

// Read data from IndexedDB
async function readData(key, _storeName) {
  try {
    const db = await initDB();
    const tx = db.transaction(_storeName, 'readonly');
    const store = tx.objectStore(_storeName);
    const data = await store.get(key);
    await tx.done;
    return data;
  } catch (error) {
    console.error('readData', error, key);
    Sentry.captureException(error);
  }
}

async function readAllData(_storeName) {
  try {
    const db = await initDB();
    const tx = db.transaction(_storeName, 'readonly');
    const store = tx.objectStore(_storeName);
    const data = await store.getAll();
    await tx.done;
    return data;
  } catch (error) {
    console.error('readAllData', error);
    Sentry.captureException(error);
  }
}

async function deleteData(_storeName, key) {
  try {
    const db = await initDB();
    const tx = db.transaction(_storeName, 'readwrite');
    const store = tx.objectStore(_storeName);
    await store.delete(key);
    await tx.done;
  } catch (error) {
    console.error('deleteData', error);
    Sentry.captureException(error);
  }
}

export {
  addData,
  deleteData,
  readData,
  readAllData,
  setData,
  recordingsStoreName,
  peaksStoreName,
  cachedAudioStoreName,
  setCacheData,
  readCacheData,
  cleanAllCachedData,
};

// EOF
